import { useContext, useEffect, useState } from "react";
import { DataContext } from "../../../app/providers/DataContext";
import { useNavigate } from "react-router-dom";
import { listaEstadosActivoBusqueda } from "../../../app/constants/constants-selectores";
import { useForm } from "react-hook-form";
import { sedeDelete, sedeMostrar, sedesBuscar } from "../api/sedes.api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { obtenerLocalStorage } from "../../../shared/utils/funciones";

import Swal from 'sweetalert2';
import { IoMdAdd } from "react-icons/io"
import { BiSearch } from "react-icons/bi"
import { useMobileDetection } from "../../../shared/hooks/useMobileDetection";
import { CONST_KEY_USUARIO_AUTENTICADO, CONST_ROL_MASTER } from "../../../app/constants/constanst-valores";
import { validarAutenticacion } from "../../auth";
import { CONST_CODIGO_SEDES_AGREGAR, CONST_CODIGO_SEDES_EDITAR, CONST_CODIGO_SEDES_ELIMINAR, CONST_RUTA_SEDES_AGREGAR, CONST_RUTA_SEDES_EDITAR, CONST_RUTA_SEDES_LISTAR, CONST_RUTA_SIN_ACCESO } from "../../../app/constants/constants-rutas-codigos";
import { rutasCodigosPrograma } from "../../../app/constants/constanst-arreglo-rutas-codigo";
import { useRefetchAlCerrarModal } from "../../../shared/hooks/useRefetchAlCerrarModal";
import { sucursalesPorCompania } from "../../sucursales";
import { usuarioObtenerCompaniasYSucursales } from "../../usuarios";
// Tabla de resultados extraída a subcomponente presentacional.
import { SedesTabla } from "../components/SedesTabla";

export const Sedes = () => {
    const usuarioAutenticado = obtenerLocalStorage(CONST_KEY_USUARIO_AUTENTICADO);
    const rol = usuarioAutenticado?.tipo

    const { listaRutasDeAcceso } = useContext(DataContext)

    const [listaCompanias, setListaCompanias] = useState<any[]>([])
    const [listaSucursales, setListaSucursales] = useState<any[]>([])

    //Estados para ver si mostrar o no los botones
    const [mostrarBotonAgregar, setMostrarBotonAgregar] = useState(false)
    const [mostrarBotonEditar, setMostrarBotonEditar] = useState(false)
    const [mostrarBotonEliminar, setMostrarBotonEliminar] = useState(false)

    const { register, handleSubmit, watch, setValue } = useForm<Record<string, any>>({
        defaultValues: {
            estado: "A",
            sede: "",
        }
    });

    // ítem 15 (React Query, fase 2): el listado de sedes se sirve vía useQuery, no useState.
    // `filtrosAplicados` = filtros efectivamente aplicados (null = aún sin buscar).
    // Disparadores (más abajo): montaje, búsqueda, post-eliminar y cierre de modal.
    const queryClient = useQueryClient()
    const [filtrosAplicados, setFiltrosAplicados] = useState<any>(null)

    const { data: sedesData, isFetching } = useQuery({
        queryKey: ["seg-sedes", filtrosAplicados],
        queryFn: () => sedesBuscar({ ...filtrosAplicados, token: usuarioAutenticado?.token }),
        enabled: filtrosAplicados !== null,
    })

    // Derivados (equivalentes 1:1 a los estados previos listaSedes/isLoading/buscar).
    const listaSedes: any[] = sedesData?.sedes ?? []
    const isLoading = isFetching
    const buscar = filtrosAplicados !== null

    const isMobile = useMobileDetection()

    const onSubmitBuscar = (data) => {
        // Aplica los filtros del formulario; useQuery re-consulta al cambiar la clave.
        // El invalidate garantiza re-fetch aunque los filtros no cambien (paridad con el flujo previo,
        // que siempre re-consultaba al pulsar buscar).
        setFiltrosAplicados({
            compania: data?.sucursal || listaSucursales?.[0]?.id,
            estado: data?.estado,
            sede: data?.sede,
        })
        queryClient.invalidateQueries({ queryKey: ["seg-sedes"] })
    };

    const navigate = useNavigate()

    const sedeAgregar = async () => {
        const rutaNavegar = CONST_RUTA_SEDES_AGREGAR
        const modulo = rutasCodigosPrograma.find(r => String(r.ruta) === String(rutaNavegar))

        const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, modulo?.codigo)

        if (validarAutenticado) {
            navigate(rutaNavegar)
        } else {
            navigate(CONST_RUTA_SIN_ACCESO)
        }

    }

    const sedeEditar = async (idSede, idCompania) => {
        const rutaNavegar = CONST_RUTA_SEDES_EDITAR
        const modulo = rutasCodigosPrograma.find(r => String(r.ruta) === String(rutaNavegar))
        const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, modulo?.codigo)

        if (validarAutenticado) {
            const dataPOST = {
                codigo: idSede,
                compania: idCompania,
                token: usuarioAutenticado?.token
            }

            const { sede } = await sedeMostrar(dataPOST)
            navigate(rutaNavegar, {
                state: sede
            })
        } else {
            navigate("/sin-acceso")
        }

    }

    const optionsEliminar = (nombreCompleto) => {
        return {
            title: `¿Estás seguro de eliminar la sede: ${nombreCompleto}?`,
            icon: 'warning' as const,
            showCancelButton: true,
            cancelButtonColor: '#6c757d',
            confirmButtonColor: '#d33',
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
        }
    }

    const sedeEliminar = (idSede, idCompania, nombreSede) => {
        Swal.fire(optionsEliminar(nombreSede)).then(async (result) => {
            if (result.isConfirmed) {
                const dataPOST = {
                    compania: idCompania,
                    codigo: idSede,
                    token: usuarioAutenticado?.token
                }
                const { status } = await sedeDelete(dataPOST)
                if (status === 1) {
                    sedesBuscarActivos()
                    Swal.fire(
                        'Sede eliminada',
                        '',
                        'success'
                    )
                } else {
                    Swal.fire(
                        'Error al eliminar sede',
                        '',
                        'success'
                    )
                }

            }
        })

    }

    // Aplica los filtros actuales del formulario (watch) y fuerza una re-consulta fresca.
    // Lo usan: la carga inicial, el post-eliminar y el cierre del modal (los datos pudieron cambiar,
    // por eso invalidamos aunque la clave no cambie).
    const sedesBuscarActivos = () => {
        setFiltrosAplicados({
            sede: watch("sede") || "",
            compania: watch("sucursal"),
            estado: watch("estado") || "A",
        })
        queryClient.invalidateQueries({ queryKey: ["seg-sedes"] })
    }

    const obtenerCompaniasYSucursales = async () => {

        const dataPOST = {
            token: usuarioAutenticado?.token
        }

        const { companias, sucursales } = await usuarioObtenerCompaniasYSucursales(dataPOST)
        setListaCompanias(companias)
        setListaSucursales(sucursales)
        setValue("compania", companias?.[0]?.id)

        return {
            companias,
            sucursales
        }
    }

    const obtenerSucursalesPorCompania = async (idCompania) => {

        if (idCompania && idCompania !== "T") {
            const dataPOST = {
                token: usuarioAutenticado?.token,
                Codigo: idCompania,
                Estado: "A"
            }


            const { sucursales } = await sucursalesPorCompania(dataPOST)

            setListaSucursales(sucursales)

            setValue("compania", idCompania)
            setValue("sucursal", sucursales?.[0]?.CompCod)
        }
    }

    useEffect(() => {

        const encontradoRutaBotonAgregar = listaRutasDeAcceso?.find(r => String(r.codigo_programa) === String(CONST_CODIGO_SEDES_AGREGAR))
        const encontradoRutaBotonEditar = listaRutasDeAcceso?.find(r => String(r.codigo_programa) === String(CONST_CODIGO_SEDES_EDITAR))
        const encontradoRutaBotonEliminar = listaRutasDeAcceso?.find(r => String(r.codigo_programa) === String(CONST_CODIGO_SEDES_ELIMINAR))

        encontradoRutaBotonAgregar ? setMostrarBotonAgregar(true) : setMostrarBotonAgregar(false)
        encontradoRutaBotonEditar ? setMostrarBotonEditar(true) : setMostrarBotonEditar(false)
        encontradoRutaBotonEliminar ? setMostrarBotonEliminar(true) : setMostrarBotonEliminar(false)
    }, [listaRutasDeAcceso])

    useEffect(() => {

        const cargarDatosIniciales = async () => {
            const { sucursales } = await obtenerCompaniasYSucursales()

            if (rol === CONST_ROL_MASTER) {
                await obtenerSucursalesPorCompania(watch("compania"));
            } else {
                const listaSucursalesFormateada = sucursales?.map(s => ({
                    CompCod: s?.id,
                    CompNom: s?.tipo
                }))
                setListaSucursales(listaSucursalesFormateada)
                setValue("sucursal", listaSucursalesFormateada?.[0]?.CompCod)
            }
            sedesBuscarActivos()
        };

        cargarDatosIniciales();

        // efecto intencional (no re-ejecutar por deps faltantes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Al cerrar el modal (agregar/editar) se refresca el listado montado de fondo.
    useRefetchAlCerrarModal(CONST_RUTA_SEDES_LISTAR, sedesBuscarActivos)

    return (
        <div className="p-3">
            <div className="container-fluid" >

                <h3 className="tw-text-center tw-text-2xl tw-font-semibold tw-text-menu-primario tw-mt-1 tw-mb-4">Sedes</h3>

                {/* Formulario */}
                <div className="tw-mb-4 tw-rounded-2xl tw-bg-white tw-p-4 tw-shadow-md tw-border tw-border-gray-100">
                    <form onSubmit={handleSubmit(onSubmitBuscar)} className="row g-2">

                        <div className="col-12 col-lg-3">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Nombre</label>
                            <input
                                {...register("sede")}
                                type="text"
                                className="form-control form-control-sm"
                                autoComplete="off"
                                placeholder="Sede"
                            />
                        </div>
                        <div className="col-12 col-lg-3">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Compañía</label>
                            <select
                                {...register("compania", {
                                    onChange: async (e) => {
                                        await obtenerSucursalesPorCompania(e.target.value)
                                    },
                                    required: true
                                })}
                                className="form-select form-select-sm"
                                value={watch("compania")}>
                                {
                                    listaCompanias?.length > 0 && listaCompanias.map(e => (
                                        <option key={e?.id} value={e?.id}>{e?.tipo}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className="col-12 col-lg-3">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Sucursal</label>
                            <select
                                {...register("sucursal", {
                                    required: true
                                })}
                                className="form-select form-select-sm"
                                value={watch("sucursal")}
                            >
                                {
                                    listaSucursales?.length > 0 && listaSucursales.map(e => (
                                        <option key={e?.CompCod} value={e?.CompCod}>{e?.CompNom}</option>
                                    ))
                                }
                                {/* <option value={"T"}>TODOS</option> */}
                            </select>
                        </div>

                        <div className="col-12 col-lg-2">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Estado</label>
                            <select {...register("estado")} className="form-select form-select-sm" >
                                {
                                    listaEstadosActivoBusqueda?.length > 0 && listaEstadosActivoBusqueda.map(e => (
                                        <option key={e?.id} value={e?.id}>{e?.tipo}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className="col-12 col-lg-1">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">&nbsp;</label>
                            <div className={isMobile ? "text-center" : ""}>
                                <button type="submit" onClick={handleSubmit(onSubmitBuscar)} style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
                                    <BiSearch size={25} className="color-iconos" />
                                </button>
                            </div>
                        </div>
                    </form>
                </div >
                {/* Fin formulario */}
                {
                    mostrarBotonAgregar && (
                        <div className="mx-auto ps-0">
                            <button className='btn btn-primario btn-sm align-middle d-flex align-items-center' onClick={sedeAgregar}>
                                <IoMdAdd size={18} />
                                <span className="ms-1">Agregar</span>
                            </button>
                        </div>
                    )
                }

                {/* Tabla de resultados */}
                <SedesTabla
                    isLoading={isLoading}
                    listaSedes={listaSedes}
                    buscar={buscar}
                    mostrarBotonEditar={mostrarBotonEditar}
                    mostrarBotonEliminar={mostrarBotonEliminar}
                    sedeEditar={sedeEditar}
                    sedeEliminar={sedeEliminar}
                />

                {/* Fin Tabla de resultados */}
            </div>
        </div>
    )
}
